import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEY_LABELS = {
  STRIPE_API_KEY: { label: "Stripe API Key", hint: "Sekret (sk_live_.../sk_test_...). Bez tego checkout dziala w trybie MOCK — klienci NIE sa obciazani." },
  STRIPE_WEBHOOK_SECRET: { label: "Stripe Webhook Signing Secret", hint: "Z Stripe Dashboard -> Developers -> Webhooks -> endpoint https://TWOJA-DOMENA/api/webhook/stripe. Nasluchuje: checkout.session.completed, invoice.paid, invoice.payment_failed, customer.subscription.deleted." },
  STRIPE_PRICE_ID_STARTER: { label: "Stripe Price ID — Starter", hint: "ID cyklicznej (rocznej) Ceny ze Stripe Dashboard dla pakietu Starter." },
  STRIPE_PRICE_ID_BUSINESS: { label: "Stripe Price ID — Business", hint: "ID cyklicznej (rocznej) Ceny ze Stripe Dashboard dla pakietu Business." },
  STRIPE_PRICE_ID_PREMIUM: { label: "Stripe Price ID — Premium", hint: "ID cyklicznej (rocznej) Ceny ze Stripe Dashboard dla pakietu Premium." },
  LLM_API_KEY: { label: "LLM API Key (Gemini)", hint: "Generowanie stron AI, AI Sales Agent, kalendarz tresci, skaner menu." },
  RESEND_API_KEY: { label: "Resend API Key", hint: "Wysylka e-maili outreach do leadow i formularza kontaktowego." },
  FACEBOOK_ACCESS_TOKEN: { label: "Facebook Access Token", hint: "Publikacja postow i statystyki strony." },
  FACEBOOK_PAGE_ID: { label: "Facebook Page ID", hint: "ID fanpage'a powiazanego z tokenem powyzej." },
  GOOGLE_CLIENT_ID: { label: "Google Client ID", hint: "Integracja z Google Business Profile." },
  GOOGLE_CLIENT_SECRET: { label: "Google Client Secret", hint: "Integracja z Google Business Profile." },
  CRM_API_TOKEN: { label: "Sorto-CRM API Token", hint: "Synchronizacja firm/leadow z sorto-crm." },
  CRM_ORG_ID: { label: "Sorto-CRM Organization ID", hint: "Opcjonalne — organizacja docelowa w CRM." },
  CMS_API_TOKEN: { label: "Directus CMS API Token", hint: "Token tylko-do-odczytu serwisowej roli w Directusie (pilot CMS, Etap 2) — generowany przez backend/scripts/directus_bootstrap.py. Bez niego promty/szablony/typy blokow uzywaja hardkodowanych wartosci z kodu." },
  DPO_CONTACT_EMAIL: { label: "Kontakt RODO (ochrona danych)", hint: "Adres e-mail wyswietlany na stronie Polityki Prywatnosci do zadan eksportu/usuniecia danych." },
  SENTRY_DSN: { label: "Sentry DSN", hint: "Monitoring bledow backendu. UWAGA: zmiana wymaga restartu backendu (SDK inicjalizuje sie raz na starcie)." },
  CUSTOM_DOMAIN_TARGET_IP: { label: "IP serwera dla wlasnych domen (Premium)", hint: "Adres IP, na ktory klienci Premium kieruja rekord A swojej domeny (np. firma.pl). Bez tego weryfikacja domen typu apex (bez www) nie zadziala — zostanie pokazany klientom jako niedostepna." },
};

export default function AdminSettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const fetchStatus = () => {
    setLoading(true);
    axios.get(`${API}/admin/settings`, { withCredentials: true })
      .then((res) => setStatus(res.data))
      .catch(() => toast.error("Błąd ładowania ustawień"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSave = async (key) => {
    const value = (drafts[key] || "").trim();
    if (!value) {
      toast.error("Wpisz wartość przed zapisem");
      return;
    }
    setSavingKey(key);
    try {
      await axios.put(`${API}/admin/settings`, { key, value }, { withCredentials: true });
      toast.success("Zapisano");
      setDrafts((d) => ({ ...d, [key]: "" }));
      fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ustawienia</h1>
        <p className="text-slate-500">Klucze API do integracji zewnętrznych. Nadpisują zmienne środowiskowe (.env) po zapisaniu tutaj — wartości nigdy nie są pokazywane w pełni po zapisie.</p>
      </div>

      <div className="space-y-4">
        {Object.keys(KEY_LABELS).map((key) => {
          const meta = KEY_LABELS[key];
          const s = status?.[key] || {};
          return (
            <Card key={key}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base">{meta.label}</CardTitle>
                  {s.configured ? (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Skonfigurowane ({s.source === "db" ? "panel" : "env"}
                      {s.hint ? `, ${s.hint}` : ""})
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1 text-slate-500">
                      <XCircle className="w-3.5 h-3.5" />
                      Brak
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">{meta.hint}</p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder={s.configured ? "•••••••••••• (wpisz, by nadpisać)" : "Wklej wartość"}
                    value={drafts[key] || ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  />
                  <Button
                    onClick={() => handleSave(key)}
                    disabled={savingKey === key}
                    size="sm"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    Zapisz
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
